import Avatar, { AvatarSize } from "@galaxy-io/dls/avatar/Avatar";
import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Chip from "@galaxy-io/dls/chips/Chip";
import { InputVariant } from "@galaxy-io/dls/inputs/Input";
import TextInput from "@galaxy-io/dls/inputs/TextInput";
import Flex, { AlignItems, FlexDirection, JustifyContent } from "@galaxy-io/dls/layout/Flex";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import ThemeSwitcher from "@galaxy-io/dls/theme/ThemeSwitcher";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import Widget, { WidgetSize } from "@galaxy-io/dls/widget/Widget";
import type { WorkspaceProfile } from "@gtm/contracts";
import type { UserPreferences } from "@gtm/contracts/core/preferences";
import { styled } from "@linaria/react";
import { PaletteIcon, UserCircleIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useRouter } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";
import { date } from "@/lib/utils";
import { logout } from "@/server/auth-actions";
import { GeneralPreferences } from "./general-preferences";
import { McpTokens } from "./mcp-tokens";
import { AccountGrid, SettingsActions, SettingsFacts, SettingsSection } from "./settings-header";

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: ${t.space[12]};
  min-width: 0;
`;
const Name = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[2]};
  min-width: 0;
`;
export function PreferencesView({
  profile,
  preferences,
}: {
  profile: WorkspaceProfile;
  preferences: UserPreferences;
}) {
  const router = useRouter();
  const { toast } = useToast();
  const [account, setAccount] = useState(profile);
  const [name, setName] = useState(profile.name);
  const [busy, setBusy] = useState(false);
  const trimmed = name.trim();
  async function save() {
    setBusy(true);
    try {
      const saved = await unwrap(apiClient().PATCH("/profile", { body: { name: trimmed } }));
      setAccount(saved);
      setName(saved.name);
      // The shell header and member lists read the stored name.
      router.refresh();
      toast({
        variant: ToastVariant.SUCCESS,
        header: "Profile updated",
        description: "Your name is updated across the workspace.",
        ...toastDefaults(ToastVariant.SUCCESS),
      });
    } catch (e) {
      toast({
        variant: ToastVariant.ERROR,
        header: "Could not update your profile",
        description: (e as Error).message,
        ...toastDefaults(ToastVariant.ERROR),
      });
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <AccountGrid>
        <SettingsSection id="profile" title="Profile">
          <Widget
            header="Profile"
            subheader="Your identity in this workspace."
            icon={UserCircleIcon}
            size={WidgetSize.LARGE}
            footer={
              <Flex
                alignItems={AlignItems.CENTER}
                justifyContent={JustifyContent.SPACE_BETWEEN}
                gap={12}
                width="100%"
              >
                <form action={logout}>
                  <Button
                    type="submit"
                    label="Sign out"
                    variant={ButtonVariant.TERTIARY}
                    size={ButtonSize.SMALL}
                  />
                </form>
                <SettingsActions>
                  <Button
                    label="Save changes"
                    isLoading={busy}
                    isDisabled={busy || !trimmed || trimmed === account.name}
                    onClick={() => void save()}
                    size={ButtonSize.SMALL}
                  />
                  {trimmed !== account.name && (
                    <Button
                      label="Cancel"
                      variant={ButtonVariant.SECONDARY}
                      isDisabled={busy}
                      onClick={() => setName(account.name)}
                      size={ButtonSize.SMALL}
                    />
                  )}
                </SettingsActions>
              </Flex>
            }
          >
            <Flex direction={FlexDirection.COLUMN} gap={24}>
              <Identity>
                <Avatar size={AvatarSize.X_LARGE} seed={account.userId} />
                <Name>
                  <Text size={TextSize.HEADING_SM} weight={TextWeight.MEDIUM} lineClamp={1}>
                    {account.name}
                  </Text>
                  <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY} lineClamp={1}>
                    {account.email || "No email on file"}
                  </Text>
                </Name>
                <Chip label={profile.role === "admin" ? "Admin" : "Member"} />
              </Identity>
              <SettingsFacts>
                <Flex direction={FlexDirection.COLUMN} gap={4}>
                  <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                    Workspace
                  </Text>
                  <Text lineClamp={1}>{profile.workspaceName}</Text>
                </Flex>
                <Flex direction={FlexDirection.COLUMN} gap={4}>
                  <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                    Member since
                  </Text>
                  <Text>{date(account.joinedAt)}</Text>
                </Flex>
              </SettingsFacts>
              <TextInput
                label="Display name"
                ariaLabel="Display name"
                value={name}
                onChange={setName}
                variant={InputVariant.BASE}
                isDisabled={busy}
                fillWidth
              />
            </Flex>
          </Widget>
        </SettingsSection>
        <Flex direction={FlexDirection.COLUMN} gap={24} minWidth={0}>
          <SettingsSection id="general" title="General">
            <GeneralPreferences preferences={preferences} />
          </SettingsSection>
          <SettingsSection id="appearance" title="Appearance">
            <Widget
              header="Appearance"
              subheader="Choose how GTM looks on this device."
              icon={PaletteIcon}
              size={WidgetSize.LARGE}
            >
              <Flex direction={FlexDirection.COLUMN} gap={12} alignItems={AlignItems.START}>
                <ThemeSwitcher />
                <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                  System follows your device’s light or dark appearance.
                </Text>
              </Flex>
            </Widget>
          </SettingsSection>
        </Flex>
      </AccountGrid>
      <McpTokens userId={profile.userId} workspaceName={profile.workspaceName} />
    </>
  );
}
